import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const CONTACT_FORM_URL = "https://readdy.ai/api/form/d7b4n3s9quqsda8ueskg";

const ContactPage: React.FC = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.message.length > 500) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams(form as Record<string, string>);
      await fetch(CONTACT_FORM_URL, { method: "POST", headers: { "Content-Type": "application/x-www-form-urlencoded" }, body: body.toString() });
      setSubmitted(true);
    } catch { setSubmitted(true); } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="text-orange-500 text-sm font-semibold uppercase tracking-widest mb-2">Get In Touch</p>
            <h1 className="text-5xl font-black text-gray-900 mb-4">Contact Us</h1>
            <p className="text-gray-500 text-lg">We&apos;re here to help with any questions about our products</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="space-y-5">
              {[
                { icon: "ri-map-pin-line", title: "Our Address", lines: ["3537 E Yale St", "Phoenix, AZ 85008"] },
                { icon: "ri-mail-line", title: "Email Us", lines: ["strayloftllc@outlook.com"] },
                { icon: "ri-time-line", title: "Business Hours", lines: ["Mon–Fri: 8am – 6pm MST", "Sat: 9am – 4pm MST"] },
              ].map((info) => (
                <div key={info.title} className="flex gap-4 p-5 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                    <i className={`${info.icon} text-orange-500`}></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm mb-1">{info.title}</p>
                    {info.lines.map((l) => <p key={l} className="text-gray-600 text-sm">{l}</p>)}
                  </div>
                </div>
              ))}
              <div className="rounded-xl overflow-hidden h-48">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3329.5!2d-112.0!3d33.45!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s3537+E+Yale+St%2C+Phoenix%2C+AZ+85008!5e0!3m2!1sen!2sus!4v1"
                  width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy"
                  title="STRAY LOFT LLC Location"
                ></iframe>
              </div>
            </div>
            <div className="lg:col-span-2">
              {submitted ? (
                <div className="h-full flex items-center justify-center bg-green-50 rounded-xl border border-green-100 p-12 text-center">
                  <div>
                    <i className="ri-checkbox-circle-line text-5xl text-green-500 block mb-4"></i>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                    <p className="text-gray-600">We&apos;ll get back to you within 1 business day.</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form className="bg-gray-50 rounded-xl p-8 border border-gray-100 space-y-5">
                  <h3 className="text-xl font-bold text-gray-900">Send a Message</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                      <input name="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 bg-white" placeholder="Your full name" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                      <input name="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 bg-white" placeholder="your@email.com" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                    <input name="subject" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 bg-white" placeholder="How can we help?" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Message * <span className="text-gray-400 font-normal">({form.message.length}/500)</span></label>
                    <textarea name="message" required rows={5} maxLength={500} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 bg-white resize-none" placeholder="Tell us about your inquiry..."></textarea>
                    {form.message.length > 500 && <p className="text-red-500 text-xs mt-1">Message cannot exceed 500 characters.</p>}
                  </div>
                  <button type="submit" disabled={submitting || form.message.length > 500}
                    className="w-full py-3 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
                    {submitting ? "Sending..." : "Send Message"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ContactPage;
